// Share to Discord (Oct 13, 2026; Ben: "Guild bot only"). A button that opens a sheet: what will be posted, the guild (when
// the player has more than one on this world) and the channel, an optional note, then Post. The Valor Tools bot posts it
// (worker/discord.ts → Valor Tools src/vweb.js). When the player can't share yet, the sheet says what's missing — a game
// account linked in Valor Tools, a Valor Tools guild on this world, or the guild's Discord — with a way to fix it.
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type DiscordStatus } from "@/api/client";
import { defaultChannel, type ShareCard, type ShareChannel } from "@/game/share";
import { fmtClock, fmtDate } from "@/game/time";
import { fmtNum } from "@/game/format";
import { usePrompts } from "@/state/prompts";
import { IconShareOut } from "./icons";
import { Sheet } from "./overlays";
import { Button, Empty, cx } from "./primitives";

const TOOLS = "https://valor-reforged-tools.com";
export const discordKeys = { status: (wid: number) => ["discord", wid] as const };

export function useDiscordStatus(wid: number, enabled: boolean) {
  return useQuery({
    queryKey: discordKeys.status(wid),
    queryFn: () => api.discord.status(wid),
    enabled: enabled && wid > 0,
    staleTime: 60_000,
    retry: 0,
  });
}

/** What's missing, in words, and where to fix it. */
function notReady(st: DiscordStatus): { text: string; action?: { label: string; href: string } } {
  const home = st.home || TOOLS;
  switch (st.reason) {
    case "link":
      return { text: "Sharing goes through Valor Tools' Discord bot. Link this game account on Valor Tools (Dashboard › Link my Valor account), then share here.", action: { label: "Open Valor Tools", href: `${home}/app/?tab=dashboard` } };
    case "guild":
      return { text: "You're not in a Valor Tools guild on this world. Join your guild on Valor Tools, then share here.", action: { label: "Open the Guild tab", href: `${home}/app/?tab=guild` } };
    case "discord":
      return { text: "Your guild hasn't linked its Discord server on Valor Tools yet. Your leader can do it in Valor Tools › Guild › Settings." };
    default:
      return { text: st.error || "Sharing to Discord isn't available right now. Try again later." };
  }
}

const stamp = (ms: number) => `${fmtDate(ms)} ${fmtClock(ms, true)} UTC`;

/** The button. `card` is built when the sheet opens (the movement or report as it is then). `link`: a report's public link,
 *  made (or found) when Post is tapped and put on the card (Oct 15 2026). */
export function DiscordShareButton({ wid, card, size = "md", kind = "steel", label = "Discord", className, link }: {
  wid: number; card: () => ShareCard; size?: "sm" | "md" | "lg"; kind?: "steel" | "gold" | "ghost" | "crimson"; label?: string; className?: string;
  link?: () => Promise<string | null>;
}) {
  const [c, setC] = useState<ShareCard | null>(null);
  return (
    <>
      <Button kind={kind} size={size} className={cx("dshare-btn", className)} aria-label="Share to Discord" title="Share to your guild's Discord" onClick={(e) => { e.stopPropagation(); setC(card()); }}>
        <IconShareOut /> {label}
      </Button>
      {c && <DiscordShareSheet wid={wid} card={c} link={link} onClose={() => setC(null)} />}
    </>
  );
}

export function DiscordShareSheet({ wid, card, onClose, link }: { wid: number; card: ShareCard; onClose: () => void; link?: () => Promise<string | null> }) {
  const qc = useQueryClient();
  const st = useDiscordStatus(wid, true);
  const guilds = useMemo(() => st.data?.guilds ?? [], [st.data]);
  const [gid, setGid] = useState<number | null>(null);
  const [ch, setCh] = useState<ShareChannel | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const g = guilds.find((x) => x.id === gid) ?? guilds[0] ?? null;
  useEffect(() => {
    if (!g) return;
    const have = g.channels.map((c) => c.key);
    if (!ch || !have.includes(ch)) setCh(defaultChannel(card, have));
  }, [g, ch, card]);

  const post = async () => {
    if (!g || !ch) return;
    setBusy(true);
    setErr(null);
    // a report goes with its public link (made now if it isn't yet); if that fails the card still goes, without it
    const url = card.url ?? (link ? await link().catch(() => null) : null);
    const r = await api.discord.share(wid, { guild: g.id, channel: ch, card: { ...card, ...(url ? { url } : {}), ...(note.trim() ? { note: note.trim().slice(0, 200) } : {}) } });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error || "Couldn't post it. Try again in a moment.");
      if (/linked any more|can't post in that channel|Link your game account/.test(r.error || "")) void qc.invalidateQueries({ queryKey: discordKeys.status(wid) });
      return;
    }
    usePrompts.getState().toast(`Posted to ${r.channel || "Discord"}${r.server ? ` in ${r.server}` : ""}.`, "success");
    onClose();
  };

  const nr = st.data && !st.data.ready ? notReady(st.data) : null;
  return (
    <Sheet open onClose={onClose} label="Share to Discord" title={<h3 className="eyebrow" style={{ margin: "0 0 12px" }}>Share to Discord</h3>}>
      <div className={cx("dshare-card", card.tone)}>
        <div className="dshare-title">{card.title}</div>
        {card.times.map((t) => <div key={t.label} className="dshare-time"><b>{t.label}</b> {stamp(t.ms)}</div>)}
        {card.fields.length > 0 && (
          <dl className="dshare-fields">
            {card.fields.slice(0, 8).map((f, i) => <div key={i}><dt>{f.name}</dt><dd>{f.value}</dd></div>)}
          </dl>
        )}
        {card.troops.length > 0 && (
          <div className="dshare-troops"><b>{card.troopsLabel || "Troops"}</b> {card.troops.map((t) => `${t.n == null ? "?" : fmtNum(t.n)}${t.lost ? ` (−${fmtNum(t.lost)})` : ""} ${t.name}`).join(" · ")}</div>
        )}
        {(card.url || link) && <div className="dshare-link">With a link to the full report{card.url ? <>: <span>{card.url.replace(/^https?:\/\//, "")}</span></> : " (anyone can open it for 7 days)"}</div>}
      </div>

      {st.isLoading && <Empty><span className="spin" /> Checking your guild's Discord…</Empty>}
      {nr && (
        <div className="dshare-missing">
          <p>{nr.text}</p>
          {nr.action && <Button kind="gold" size="md" onClick={() => window.open(nr.action!.href, "_blank", "noopener")}>{nr.action.label}</Button>}
        </div>
      )}
      {st.data?.ready && g && (
        <>
          {guilds.length > 1 && (
            <div className="dshare-pick" role="radiogroup" aria-label="Guild">
              {guilds.map((x) => (
                <button key={x.id} type="button" role="radio" aria-checked={x.id === g.id} className={cx("dshare-chip", x.id === g.id && "on")} onClick={() => setGid(x.id)}>
                  {x.tag ? `[${x.tag}] ` : ""}{x.name}
                </button>
              ))}
            </div>
          )}
          <div className="dshare-label">Post in {g.dcName ? <b>{g.dcName}</b> : "your guild's Discord"}</div>
          <div className="dshare-pick" role="radiogroup" aria-label="Channel">
            {g.channels.map((c) => (
              <button key={c.key} type="button" role="radio" aria-checked={c.key === ch} className={cx("dshare-chip", c.key === ch && "on")} onClick={() => setCh(c.key)}>
                {c.name ? `#${c.name}` : c.label}
              </button>
            ))}
          </div>
          <div className="field">
            <label htmlFor="dshare-note">Note (optional)</label>
            <input id="dshare-note" className="input" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note for your guild" />
          </div>
          {err && <div className="dshare-err" role="alert">{err}</div>}
          <div className="c-grid2" style={{ marginTop: 12 }}>
            <Button kind="steel" onClick={onClose}>Cancel</Button>
            <Button kind="gold" disabled={busy || !ch} onClick={() => void post()}>{busy ? "Posting…" : "Post"}</Button>
          </div>
          <p className="dshare-foot">Posted by the Valor Tools bot with your name. Times show in each reader's own time on Discord (UTC here).</p>
        </>
      )}
    </Sheet>
  );
}
